<template>
  <section
    id="bons-plans"
    class="bons-plans"
  >
    <div class="container">
      <SectionHead
        :icon="data.bonsPlans.icon"
        :title="data.bonsPlans.title"
      />

      <div class="bons-plans__grid">
        <article
          v-for="(item, i) in visibleItems"
          :key="i"
          class="plan-card"
          :class="{ 'is-open': openIndex === i }"
          @click="toggleOpen(i)"
        >
          <div class="plan-card__image">
            <img
              v-if="item.image"
              :src="item.image"
              :alt="item.shop"
            >
            <div
              v-if="item.hoverTitle || item.hoverText || item.hoverNote"
              class="plan-card__over"
            >
              <p
                v-if="item.hoverTitle"
                class="plan-card__over-title"
              >{{ item.hoverTitle }}</p>
              <p
                v-for="(par, j) in paragraphs(item.hoverText)"
                :key="j"
                class="plan-card__over-text"
              >{{ par }}</p>
              <p
                v-if="item.hoverNote"
                class="plan-card__over-note"
              >{{ item.hoverNote }}</p>
            </div>
          </div>
          <div class="plan-card__body">
            <span class="plan-card__logo">
              <img
                v-if="item.logo"
                :src="item.logo"
                :alt="item.shop"
              >
            </span>
            <div class="plan-card__text">
              <p class="plan-card__shop">{{ item.shop }}</p>
              <p
                v-if="item.offerTitle"
                class="plan-card__offer-title"
              >{{ item.offerTitle }}</p>
              <p
                v-if="item.offer"
                class="plan-card__offer"
              >{{ item.offer }}</p>
            </div>
          </div>
        </article>
      </div>

      <div
        v-if="hasMore"
        class="bons-plans__more"
      >
        <button
          class="btn-teal"
          @click="showAll = true"
        >{{ data.bonsPlans.moreLabel }}</button>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHead from '@/components/SectionHead.vue'
import { scheduled } from '@/content/ccContent'

const INITIAL_COUNT = 6

// vrai survol disponible (souris, pavé tactile) ; faux sur mobile/tablette
const canHover = () => window.matchMedia('(hover: hover)').matches

export default {
  name: 'BonsPlansSection',
  components: { SectionHead },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showAll: false,
      // équivalent tactile du survol (mobile/tablette)
      openIndex: null
    }
  },
  methods: {
    // écrans tactiles : 1er appui ouvre le survol, 2e appui le referme. À la
    // souris, le survol suffit (un clic ne doit pas le figer ouvert).
    toggleOpen(i) {
      if (canHover()) return
      this.openIndex = this.openIndex === i ? null : i
    },
    // paragraphes séparés par une ligne vide ; un retour simple reste un saut de ligne
    paragraphs(text) {
      return (text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    }
  },
  computed: {
    // offres en cours seulement ; les permanentes passent en dernier
    activeItems() {
      return scheduled(this.data.bonsPlans.items)
    },
    visibleItems() {
      return this.showAll ? this.activeItems : this.activeItems.slice(0, INITIAL_COUNT)
    },
    hasMore() {
      return !this.showAll && this.activeItems.length > INITIAL_COUNT
    }
  }
}
</script>

<style lang="scss" scoped>
.bons-plans {
  background: $white;
  padding: 64px 0 72px;
}

// Grille et typographie relevées sur le PSD « bons plans » (plan de travail 2200) :
// mise en page à l'échelle du conteneur (x0,815), textes à l'échelle des titres
// du site (x0,68 : titre de section 46,9 pt -> 32 px).
.bons-plans__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 56px 16px;
  margin-top: 40px;
}

// pas de boîte autour de la carte : image arrondie, puis logo + texte sur le fond
.plan-card {
  cursor: pointer;

  &__image {
    aspect-ratio: 16 / 9;
    background: #D9DFE5;
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    // le texte du survol se dimensionne sur la largeur de la carte (unités cqw) :
    // la composition garde ses proportions quand la carte rétrécit
    container-type: inline-size;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  // survol : dégradé du PSD (#1C8DAA -> #71B571, sarcelle à gauche) à 90 % d'opacité,
  // porté par le fond seul pour que le texte reste pleinement opaque.
  // .is-open = équivalent tactile.
  &__over {
    position: absolute;
    inset: 0;
    background: linear-gradient(to left, rgba(#71B571, 0.9) 0%, rgba(#1C8DAA, 0.9) 71%);
    color: $white;
    padding: 4.3cqw 6.5cqw 2.7cqw;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    scrollbar-width: thin;
    opacity: 0;
    transition: opacity 0.25s ease;

    &-title {
      font-size: clamp(12px, 3.92cqw, 16px);
      font-weight: 700;
      line-height: 1.25;
      text-transform: uppercase;
    }

    &-text {
      margin-top: 1em;
      font-size: clamp(12px, 3.92cqw, 16px);
      font-weight: 700;
      line-height: 1.3;
      white-space: pre-line;
    }

    &-note {
      margin-top: 0.8em;
      font-size: clamp(9px, 2.8cqw, 11.5px);   // 15,24 pt x 0,68 à 1440 px
      font-weight: 700;
      line-height: 1.2;
    }
  }

  // :hover limité aux appareils qui survolent vraiment : sur écran tactile il
  // resterait collé après un appui et le 2e appui ne refermerait rien
  &.is-open &__over {
    opacity: 1;
  }

  @media (hover: hover) {
    &:hover &__over {
      opacity: 1;
    }
  }

  &__body {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    padding-top: 16px;
  }

  // tuile logo : le visuel carré remplit la tuile (fond de marque inclus)
  &__logo {
    flex: 0 0 auto;
    width: 64px;
    height: 64px;
    border: 1px solid rgba($navy, 0.15);
    border-radius: 10px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__text {
    padding-top: 2px;
    line-height: 1.5;
  }

  &__shop {
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    color: $ink;
  }

  &__offer-title {
    font-size: 13px;
    font-weight: 700;
    color: $ink;
  }

  &__offer {
    font-size: 13px;
    color: $ink;
  }
}

.bons-plans__more {
  display: flex;
  justify-content: center;
  margin-top: 38px;
}

@include tablet-down {
  .bons-plans__grid {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 40px;
  }
}

@include mobile {
  .bons-plans {
    padding: 48px 0 56px;
  }

  .bons-plans__grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}
</style>
